import { isWebGameUploadsApiPath, normalizeWebGameUploadStoredPath } from './webGameUploads';

export const SLOT_MACHINE_GAME_CODE = 'slot-machine';
export const SLOT_MACHINE_SYMBOL_UPLOADS_FOLDER = 'uploads_api/webgames/slot-machine';

export type SlotMachineSymbolImageEditor = {
    id: string;
    label: string;
    imageUrl: string;
    defaultImageUrl: string;
};

export const DEFAULT_SLOT_MACHINE_SYMBOL_IMAGE_URLS: Readonly<Record<string, string>> = {
    soccer: '/webgames/assets/slot-machine/soccer.svg',
    basketball: '/webgames/assets/slot-machine/soccer.svg',
    ticket: '/webgames/assets/slot-machine/ticket.svg',
    trophy: '/webgames/assets/slot-machine/trophy.svg',
    betcoin: '/webgames/assets/betcoin.svg',
    cash: '/webgames/assets/slot-machine/cash.svg',
    jackpot: '/webgames/assets/slot-machine/jackpot.svg',
};

export const SLOT_MACHINE_SYMBOL_EDITOR_ORDER: readonly string[] = [
    'soccer',
    'basketball',
    'ticket',
    'trophy',
    'betcoin',
    'cash',
    'jackpot',
];

function defaultImageUrlForSymbolId(symbolId: string): string {
    const normalizedId = symbolId.trim();
    if (!normalizedId) {
        return '/webgames/assets/slot-machine/soccer.svg';
    }
    return DEFAULT_SLOT_MACHINE_SYMBOL_IMAGE_URLS[normalizedId]
        ?? `/webgames/assets/slot-machine/${normalizedId}.svg`;
}

export function slotMachineSymbolUploadStoredPath(symbolId: string, extension = 'png'): string {
    const normalizedId = symbolId.trim();
    if (!normalizedId) {
        return '';
    }
    const normalizedExtension = extension.trim().replace(/^\./, '') || 'png';
    return `${SLOT_MACHINE_SYMBOL_UPLOADS_FOLDER}/${normalizedId}.${normalizedExtension}`;
}

function symbolRecordFromUnknown(value: unknown): Record<string, unknown> | null {
    if (value == null || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }
    return value as Record<string, unknown>;
}

function symbolIdFromRecord(record: Record<string, unknown>): string {
    return typeof record.id === 'string' ? record.id.trim() : '';
}

function symbolLabelFromRecord(record: Record<string, unknown>, fallbackId: string): string {
    if (typeof record.label === 'string' && record.label.trim()) {
        return record.label.trim();
    }
    return fallbackId;
}

function normalizeSymbolImageStoredPath(pathOrUrl: string): string {
    const trimmed = pathOrUrl.trim();
    if (!trimmed) {
        return '';
    }
    if (!isWebGameUploadsApiPath(trimmed)) {
        return trimmed;
    }
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        return trimmed;
    }
    return normalizeWebGameUploadStoredPath(trimmed);
}

function symbolImageUrlFromRecord(record: Record<string, unknown>): string {
    if (typeof record.imageUrl !== 'string') {
        return '';
    }
    return normalizeSymbolImageStoredPath(record.imageUrl);
}

export function slotSymbolImagesFromMetadata(
    metadata: Record<string, unknown> | null | undefined
): SlotMachineSymbolImageEditor[] {
    const rawSymbols = metadata?.symbols;
    const byId = new Map<string, SlotMachineSymbolImageEditor>();

    if (Array.isArray(rawSymbols)) {
        rawSymbols.forEach(rawSymbol => {
            const record = symbolRecordFromUnknown(rawSymbol);
            if (record == null) return;
            const id = symbolIdFromRecord(record);
            if (!id) return;
            byId.set(id, {
                id,
                label: symbolLabelFromRecord(record, id),
                imageUrl: symbolImageUrlFromRecord(record),
                defaultImageUrl: defaultImageUrlForSymbolId(id),
            });
        });
    }

    const ordered: SlotMachineSymbolImageEditor[] = SLOT_MACHINE_SYMBOL_EDITOR_ORDER.map(id => (
        byId.get(id) ?? {
            id,
            label: id,
            imageUrl: '',
            defaultImageUrl: defaultImageUrlForSymbolId(id),
        }
    ));

    const extras = Array.from(byId.values())
        .filter(symbol => !SLOT_MACHINE_SYMBOL_EDITOR_ORDER.includes(symbol.id))
        .sort((left, right) => left.id.localeCompare(right.id));

    return [...ordered, ...extras];
}

export function mergeSlotSymbolImagesIntoMetadata(
    metadata: Record<string, unknown>,
    images: ReadonlyArray<SlotMachineSymbolImageEditor>
): Record<string, unknown> {
    const existingSymbols = Array.isArray(metadata.symbols) ? [...metadata.symbols] : [];
    const indexById = new Map<string, number>();

    existingSymbols.forEach((rawSymbol, index) => {
        const record = symbolRecordFromUnknown(rawSymbol);
        if (record == null) return;
        const id = symbolIdFromRecord(record);
        if (!id) return;
        indexById.set(id, index);
    });

    images.forEach(image => {
        const configuredUrl = normalizeSymbolImageStoredPath(image.imageUrl);
        const nextImageUrl = configuredUrl || image.defaultImageUrl;
        const existingIndex = indexById.get(image.id);
        if (existingIndex != null) {
            const existingRecord = symbolRecordFromUnknown(existingSymbols[existingIndex]);
            if (existingRecord == null) return;
            existingSymbols[existingIndex] = {
                ...existingRecord,
                imageUrl: nextImageUrl,
            };
            return;
        }

        existingSymbols.push({
            id: image.id,
            label: image.label,
            imageUrl: nextImageUrl,
            weight: 1,
            payouts: [],
        });
        indexById.set(image.id, existingSymbols.length - 1);
    });

    return {
        ...metadata,
        symbols: existingSymbols,
    };
}
